import {memo, useMemo} from "react";
import CakeOutlined from "@mui/icons-material/CakeOutlined";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import CachedIcon from "@mui/icons-material/Cached";
import LoginOutlined from "@mui/icons-material/LoginOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import {Box} from "@mui/material";
import {useTranslation} from "react-i18next";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {GuestCount, Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import {labelizeUnderScoreText} from "crqlar-react-common/dist/utils/text.util";
import {useTimeframe} from "../hooks/useTimeframe";
import {
    ALLERGY_ICON_MAP,
    getAccommodationStayIndicators,
    getReservationDisplay
} from "../utils/reservation-utils";

interface Props {
    reservation?: Reservation;
    profiles?: Profile[];
    accommodationResources?: Resource[];
    accommodationReservations?: Reservation[];
    guestCounts?: GuestCount[];
    tableReservationCount?: number;
    onClickEdit?: () => void;
    onClickView?: () => void;
    showDateFormat?: boolean;
}

const TableOverlayCardBody: React.FC<Props> = ({
                                                   reservation,
                                                   profiles = [],
                                                   accommodationResources = [],
                                                   accommodationReservations = [],
                                                   guestCounts,
                                                   tableReservationCount,
                                                   onClickEdit,
                                                   onClickView,
                                                   showDateFormat = false
                                               }) => {
    const {t} = useTranslation();
    const {date: selectedDate} = useTimeframe();
    const {
        guestDisplayName,
        guestCount,
        primaryLanguage,
        roomNames,
        roomNamesInline,
        fromBookingEngine,
        timeRange,
        birthdayNames,
        isBirthday,
        preferences,
    } = getReservationDisplay({
        reservation,
        profiles,
        accommodationResources,
        guestCounts,
        showDateFormat
    });

    const displayName = guestDisplayName || "-";
    const guestCountValue = guestCount ?? 0;
    const guestCountLabel = t("Guest count short", {count: guestCountValue});
    const guestCountTitle = t("Guest count title", {count: guestCountValue});
    const normalizedRoomNames = roomNames?.trim() || "";
    const inlineRoomNames = roomNamesInline?.trim() || "";
    const birthdayTitle = birthdayNames.join(", ");
    const isSerial = Boolean(reservation?.reservationGroupId);
    const hasMultipleTableReservations = (tableReservationCount ?? 0) > 1;
    const linkedReservationCountTitle = t("Linked reservations title", {count: tableReservationCount ?? 0});
    const {
        stayStart,
        stayEnd,
        checksInOnDate: checksInToday,
        checksOutOnDate: checksOutToday,
    } = useMemo(
        () => getAccommodationStayIndicators(accommodationReservations, selectedDate),
        [accommodationReservations, selectedDate],
    );
    const preferenceIndicators = Array.from(new Set(
        preferences.map(preference => preference.preferenceType).filter(Boolean),
    )).flatMap(type => {
        const mapping = ALLERGY_ICON_MAP[type!];
        if (!mapping) return [];

        const values = Array.from(new Set(
            preferences
                .filter(preference => preference.preferenceType === type)
                .map(preference => preference.preferenceValue?.trim())
                .filter((value): value is string => Boolean(value))
                .map(labelizeUnderScoreText),
        ));

        return [{
            type: type!,
            title: values.join(", ") || t("Preferences"),
            ...mapping,
        }];
    });
    const hasBottomLeftBadges =
        checksInToday || isBirthday || preferenceIndicators.length > 0 || checksOutToday;

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                bgcolor: 'common.white',
                borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                width: 112,
                height: 64,
            }}
        >
            <Box
                onClick={onClickEdit}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    py: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    boxSizing: 'border-box',
                    bgcolor: 'common.white',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: theme => theme.transitions.create(
                        ['border-color', 'box-shadow'],
                        {duration: theme.transitions.duration.shorter},
                    ),
                    '&:hover': {
                        bgcolor: 'common.white',
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 6px rgba(15, 23, 42, 0.16)',
                    },
                }}
            >
                <Box
                    sx={{
                        px: 0.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 13,
                    }}
                >
                    <span
                        style={{
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minWidth: 0,
                        }}
                        title={displayName}
                    >
                        {displayName}
                    </span>
                </Box>

                <Box
                    sx={{
                        px: 0.5,
                        display: 'flex',
                        justifyContent: 'center',
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            maxWidth: '100%',
                            flex: '0 1 auto',
                            minWidth: 0,
                        }}
                    >
                        <span title={guestCountTitle} style={{flexShrink: 0}}>{guestCountLabel}</span>
                        {!primaryLanguage && normalizedRoomNames && <span style={{flexShrink: 0}}>|</span>}
                        {primaryLanguage && (
                            <img
                                alt={getNameForLanguage(primaryLanguage)}
                                title={getNameForLanguage(primaryLanguage)}
                                src={getIconForLanguage(primaryLanguage)}
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '0.5px solid rgba(0,0,0,0.15)',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        {normalizedRoomNames && (
                            <span
                                title={normalizedRoomNames}
                                style={{
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {inlineRoomNames}
                            </span>
                        )}
                        {fromBookingEngine && (
                            <BookOnlineIcon
                                titleAccess={t('Online Reservation')}
                                style={{fontSize: 12, flexShrink: 0, color: '#555'}}
                            />
                        )}
                    </div>
                </Box>

                <Box
                    sx={{
                        px: 0.5,
                        fontSize: 11,
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                    title={timeRange}
                >
                    {timeRange}
                </Box>

                {hasBottomLeftBadges && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: -5,
                            display: 'flex',
                            alignItems: 'center',
                            left: 2,
                            gap: 2,
                        }}
                    >
                        {checksInToday && (
                            <div
                                title={t('{{name}} checks in at {{time}}', {
                                    name: displayName,
                                    time: stayStart ? formatDateHM(stayStart) : '—',
                                })}
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    color: '#0288d1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 5px rgba(15,23,42,0.32)',
                                }}
                            >
                                <LoginOutlined style={{fontSize: 11}}/>
                            </div>
                        )}
                        {isBirthday && (
                            <div
                                title={birthdayTitle}
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    color: '#9c27b0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 5px rgba(15,23,42,0.32)',
                                }}
                            >
                                <CakeOutlined style={{fontSize: 11}}/>
                            </div>
                        )}
                        {preferenceIndicators.map(({type, title, icon: Icon, color}) => (
                            <div
                                key={type}
                                title={title}
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 5px rgba(15,23,42,0.32)',
                                }}
                            >
                                <Icon style={{fontSize: 11}}/>
                            </div>
                        ))}
                        {checksOutToday && (
                            <div
                                title={t('{{name}} checks out at {{time}}', {
                                    name: displayName,
                                    time: stayEnd ? formatDateHM(stayEnd) : '—',
                                })}
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    color: '#ef6c00',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 5px rgba(15,23,42,0.32)',
                                }}
                            >
                                <LogoutOutlined style={{fontSize: 11}}/>
                            </div>
                        )}
                    </div>
                )}

                {(isSerial || hasMultipleTableReservations) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -5,
                            right: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                        }}
                    >
                        {isSerial && (
                            <Box
                                component="span"
                                title={t("Slot type SERIAL")}
                                sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: 'common.white',
                                    color: 'text.primary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 5px rgba(15,23,42,0.32)',
                                }}
                            >
                                <CachedIcon sx={{fontSize: 13}}/>
                            </Box>
                        )}

                        {hasMultipleTableReservations && (
                            <Box
                                component="button"
                                type="button"
                                title={linkedReservationCountTitle}
                                aria-label={linkedReservationCountTitle}
                                onPointerDown={event => event.stopPropagation()}
                                onClick={event => {
                                    event.stopPropagation();
                                    onClickView?.();
                                }}
                                sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: '#111',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 10,
                                    lineHeight: 1,
                                    padding: 0,
                                    border: 0,
                                    fontFamily: 'inherit',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 5px rgba(15,23,42,0.32)',
                                    '&:hover, &:focus-visible': {
                                        bgcolor: '#333',
                                    },
                                    '&:focus-visible': {
                                        outline: '2px solid #fff',
                                        outlineOffset: 1,
                                    },
                                }}
                            >
                                {tableReservationCount}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default memo(TableOverlayCardBody);
